import Button from '@components/V4/Button';
import { Comment, Enrollment, ENROLLMENT_STATUS_TYPES } from '@shared/common';
import moment from 'moment';
import { useState } from 'react';

interface EnrollmentDetailsProps {
  enrollmentDetails: EnrollmentDetailsType;
}

export interface EnrollmentDetailsType {
  startDate: string;
  endDate: string;
  lastStatus?: Enrollment['lastStatus'];
  noOfSessions?: number;
  comment?: Comment;
}

const EnrollmentDetails = (props: EnrollmentDetailsProps) => {
  const { enrollmentDetails } = props;

  const [showFullComment, setShowFullComment] = useState(false);
  const [showFullReason, setShowFullReason] = useState(false);

  const charThreshold = 100;

  const commentText = enrollmentDetails.comment?.message ?? 'n/a';
  const commentDate = enrollmentDetails.comment?.createdAt;
  const reasonText = enrollmentDetails.lastStatus?.reason ?? 'n/a';

  const isLongComment = commentText.split('\n').length > 2 || commentText.length > charThreshold;
  const isLongReason = reasonText.split('\n').length > 2 || reasonText.length > charThreshold;

  const getTruncatedComment = (text: string) => {
    const firstNewlineIndex = text.indexOf('\n');
    const secondNewlineIndex =
      firstNewlineIndex !== -1 ? text.indexOf('\n', firstNewlineIndex + 1) : -1;

    return secondNewlineIndex !== -1
      ? text.slice(0, secondNewlineIndex)
      : text.slice(0, charThreshold);
  };

  const displayedComment = showFullComment ? commentText : getTruncatedComment(commentText);
  const displayedCommentDate = commentDate
    ? moment(commentDate).format('DD.MM.YY HH:mm')
    : 'Unknown date';
  const displayedReason = showFullReason ? reasonText : getTruncatedComment(reasonText);

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="w-full flex gap-x-8">
        <div className="flex flex-col gap-2 w-1/2">
          <div className="flex text-customGrey">
            <span className="min-w-max">Start Date:</span>
            <span className="text-white ml-2 break-words">
              {enrollmentDetails.startDate
                ? moment(enrollmentDetails.startDate).format('DD.MM.YYYY')
                : ' n/a'}
            </span>
          </div>

          <div className="flex text-customGrey">
            <span className="min-w-max">End Date:</span>
            <span className="text-white ml-2 break-words">
              {enrollmentDetails.endDate
                ? moment(enrollmentDetails.endDate).format('DD.MM.YYYY')
                : ' n/a'}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex text-customGrey">
              <div className="flex flex-col min-w-max">
                <span>Comment:</span>
                {commentDate && (
                  <span className="text-xs italic">{commentDate && `${displayedCommentDate}`}</span>
                )}
              </div>
              <span className="text-white ml-2 break-words whitespace-pre-wrap">
                {displayedComment}
                {isLongComment && !showFullComment && (
                  <>
                    <br />
                    ...
                  </>
                )}
              </span>
            </div>
          </div>
          {isLongComment && (
            <Button
              variant="text"
              className="text-customPrimary ml-auto"
              buttonText={showFullComment ? 'Show Less' : 'Show More'}
              onClick={() => setShowFullComment(!showFullComment)}
            />
          )}
        </div>

        <div className="flex flex-col gap-2 w-1/2">
          <div className="flex text-customGrey">
            <span className="min-w-max">Total Sessions:</span>
            <span className="text-white ml-2 break-words">{enrollmentDetails.noOfSessions}</span>
          </div>

          <div className="flex text-customGrey">
            <span className="min-w-max">Status:</span>
            <span className="text-white ml-2 break-words">
              {enrollmentDetails.lastStatus !== undefined
                ? ENROLLMENT_STATUS_TYPES[enrollmentDetails.lastStatus.status]
                : 'n/a'}
            </span>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex text-customGrey">
              <span className="min-w-max">Reason:</span>
              <span className="text-white ml-2 break-words whitespace-pre-wrap">
                {displayedReason}
                {isLongReason && !showFullReason && (
                  <>
                    <br />
                    ...
                  </>
                )}
              </span>
            </div>
          </div>
          {isLongReason && (
            <Button
              variant="text"
              className="text-customPrimary ml-auto"
              buttonText={showFullReason ? 'Show Less' : 'Show More'}
              onClick={() => setShowFullReason(!showFullReason)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EnrollmentDetails;
